import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { htmlSafe } from '@ember/template';
import { concat } from '@ember/helper';
import { eq } from 'tio-ui/utilities';
import PageHero from 'tio-reimagined/components/page-hero';
import SectionHeader from 'tio-reimagined/components/section-header';
import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import {
  PhHeart,
  PhTrendUp,
  PhArrowRight,
  PhChartLineUp,
  PhBrain,
  PhTarget,
  PhSparkle,
  PhLightbulb,
  PhPiggyBank,
  PhScales,
} from 'ember-phosphor-icons';

interface WellnessModel {
  dataSourceInfo: DataSourceInfo;
}

interface WellnessSignature {
  Args: {
    model: WellnessModel;
  };
}

interface WellnessScore {
  category: string;
  score: number;
  maxScore: number;
  status: 'excellent' | 'good' | 'needs-work';
  tip: string;
}

const DEMO_SCORES: WellnessScore[] = [
  {
    category: 'Debt Management',
    score: 78,
    maxScore: 100,
    status: 'good',
    tip: 'Making consistent payments. Consider increasing by $50/mo to save $4,200 in interest.',
  },
  {
    category: 'Benefits Utilization',
    score: 62,
    maxScore: 100,
    status: 'needs-work',
    tip: "You're using 62% of available benefits. Explore tuition assistance to maximize your $5,250.",
  },
  {
    category: 'Emergency Fund',
    score: 45,
    maxScore: 100,
    status: 'needs-work',
    tip: 'Build toward 3-6 months of expenses. Start with automatic transfers of $100/month.',
  },
  {
    category: 'Retirement Readiness',
    score: 85,
    maxScore: 100,
    status: 'excellent',
    tip: "Great work! You're on track for retirement. Consider increasing contribution by 1%.",
  },
];

export default class WellnessPage extends Component<WellnessSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get scores() {
    return DEMO_SCORES;
  }

  get overallScore() {
    const total = this.scores.reduce((sum, s) => sum + s.score, 0);
    return Math.round(total / this.scores.length);
  }

  get smartActions(): SmartAction[] {
    return [
      {
        id: 'boost-score',
        title: 'Boost Your Score',
        description: 'Follow personalized recommendations to improve your financial wellness.',
        icon: PhTrendUp,
        priority: 'urgent',
        category: 'money',
        cta: 'Get Started',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'maximize-benefits',
        title: 'Maximize Benefits',
        description: "You're leaving $2,100 in benefits unused. See what you're missing.",
        icon: PhSparkle,
        priority: 'high',
        category: 'opportunity',
        cta: 'Explore Benefits',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'set-goals',
        title: 'Set Financial Goals',
        description: 'Create targets for debt payoff, savings, and retirement contributions.',
        icon: PhTarget,
        priority: 'medium',
        category: 'money',
        cta: 'Set Goals',
        route: 'authenticated.reimagined.advisor',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <DataIndicator
      @source="placeholder"
      @showBadge={{true}}
      @dataSourceId="financial-wellness-score"
    >
      <VStack class="gap-8 pb-12">
        {{! Hero Section }}
        <PageHero @theme="rose" @decoration="circles" @rounded="2xl">
          <:default>
            <div class="flex items-center gap-3 mb-4">
              <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
                <PhHeart class="h-7 w-7 text-white" />
              </div>
              <div>
                <Heading @level={{2}} class="text-white text-2xl font-bold">
                  Financial Wellness
                </Heading>
                <Text class="text-rose-100 text-sm">
                  Your complete financial health at a glance
                </Text>
              </div>
            </div>

            <div class="flex items-center gap-8 mt-6">
              <div class="text-center">
                <div class="text-5xl font-bold text-white">{{this.overallScore}}</div>
                <Text class="text-rose-200 text-sm">Wellness Score</Text>
              </div>
              <div class="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                {{#each this.scores as |s|}}
                  <div class="bg-white/10 rounded-xl p-3">
                    <Text class="text-rose-200 text-xs">{{s.category}}</Text>
                    <div class="flex items-baseline gap-1 mt-1">
                      <span
                        class="text-lg font-bold
                          {{if
                            (eq s.status 'excellent')
                            'text-emerald-300'
                            (if (eq s.status 'good') 'text-amber-300' 'text-red-300')
                          }}"
                      >{{s.score}}</span>
                      <span class="text-rose-300 text-xs">/{{s.maxScore}}</span>
                    </div>
                  </div>
                {{/each}}
              </div>
            </div>
          </:default>
        </PageHero>

        {{! Smart Actions }}
        <SectionHeader
          @icon={{PhLightbulb}}
          @title="Recommended Actions"
          @subtitle="Steps to improve your wellness score"
          @iconClass="text-amber-500"
        />
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          {{#each this.smartActions as |action|}}
            <SmartActionCard @item={{action}} />
          {{/each}}
        </div>

        {{! Detailed Scores }}
        <SectionHeader
          @icon={{PhChartLineUp}}
          @title="Detailed Breakdown"
          @subtitle="Score details and improvement tips"
          @iconClass="text-blue-500"
        />
        <div class="space-y-4">
          {{#each this.scores as |score|}}
            <div
              class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
            >
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-3">
                  <Heading @level={{4}} class="font-semibold">{{score.category}}</Heading>
                  <Chip
                    @intent={{if
                      (eq score.status "excellent")
                      "success"
                      (if (eq score.status "good") "warning" "danger")
                    }}
                  >
                    {{score.status}}
                  </Chip>
                </div>
                <div class="text-lg font-bold">
                  <span
                    class="{{if
                        (eq score.status 'excellent')
                        'text-emerald-600'
                        (if (eq score.status 'good') 'text-amber-600' 'text-red-600')
                      }}"
                  >{{score.score}}</span>
                  <span class="text-gray-400">/{{score.maxScore}}</span>
                </div>
              </div>
              {{! Progress bar }}
              <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                <div
                  class="h-2 rounded-full transition-all
                    {{if
                      (eq score.status 'excellent')
                      'bg-emerald-500'
                      (if (eq score.status 'good') 'bg-amber-500' 'bg-red-500')
                    }}"
                  style={{htmlSafe (concat "width: " score.score "%")}}
                ></div>
              </div>
              <div class="flex items-start gap-2">
                <PhLightbulb class="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <Text class="text-gray-600 dark:text-gray-400 text-sm">
                  {{score.tip}}
                </Text>
              </div>
            </div>
          {{/each}}
        </div>

        {{! Learning Resources }}
        <SectionHeader
          @icon={{PhBrain}}
          @title="Financial Learning"
          @subtitle="Build knowledge, build wealth"
          @iconClass="text-purple-500"
        />
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
          >
            <div class="flex items-center gap-3 mb-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30"
              >
                <PhBrain class="h-5 w-5 text-blue-600" />
              </div>
              <Heading @level={{4}} class="font-semibold">Budgeting 101</Heading>
            </div>
            <Text class="text-gray-600 dark:text-gray-400 text-sm">
              Master the fundamentals of tracking expenses and building a budget that works.
            </Text>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
          >
            <div class="flex items-center gap-3 mb-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30"
              >
                <PhPiggyBank class="h-5 w-5 text-green-600" />
              </div>
              <Heading @level={{4}} class="font-semibold">Emergency Fund</Heading>
            </div>
            <Text class="text-gray-600 dark:text-gray-400 text-sm">
              Learn how to build a safety net that protects you from unexpected expenses.
            </Text>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
          >
            <div class="flex items-center gap-3 mb-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30"
              >
                <PhScales class="h-5 w-5 text-purple-600" />
              </div>
              <Heading @level={{4}} class="font-semibold">Debt vs Savings</Heading>
            </div>
            <Text class="text-gray-600 dark:text-gray-400 text-sm">
              When should you pay off debt vs save? Learn the math behind this common dilemma.
            </Text>
          </div>
        </div>

        {{! CTA }}
        <div
          class="bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 rounded-2xl p-8 text-center"
        >
          <Heading @level={{3}} class="text-xl font-bold mb-2">
            Get Personalized Guidance
          </Heading>
          <Text class="text-gray-600 dark:text-gray-400 mb-6">
            Our AI advisor can analyze your complete financial picture and recommend specific
            actions to improve each wellness category.
          </Text>
          <LinkTo @route="authenticated.reimagined.advisor">
            <Button @intent="primary">
              Talk to Advisor
              <PhArrowRight class="ml-2 h-4 w-4" />
            </Button>
          </LinkTo>
        </div>
      </VStack>
    </DataIndicator>
  </template>
}
